import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils.js"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Raise above another open dialog (nested modals). */
  stacked?: boolean;
  /** Hide the built-in top-right close control (use a custom one instead). */
  hideCloseButton?: boolean;
  /**
   * "default": fills the screen on phones (below 640px), centred card above.
   * "confirm": always a centred card (short confirms and alerts).
   */
  size?: "default" | "confirm";
};

/**
 * Below 640px a dialog takes the whole screen: the transform centring is
 * undone, it is 100dvh tall (iOS toolbars excluded) and square-cornered.
 * `max-sm:` variants sit after base utilities in the stylesheet, so they beat
 * a dialog's own desktop width/height classes without touching each dialog.
 */
const phoneFullScreenClass =
  "max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0 max-sm:overflow-y-auto max-sm:overscroll-contain max-sm:pb-[env(safe-area-inset-bottom)]"

const confirmCardClass = "w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg"

/**
 * True inside an open DialogContent. A modal dialog locks page scrolling with
 * react-remove-scroll, which cancels wheel events outside the dialog node —
 * so popovers portaled out of the dialog read this and add their own scroll
 * lock, which whitelists their content (see popover.tsx).
 */
export const InsideDialogContext = React.createContext(false)

type OutsideEvent = { target: EventTarget | null; preventDefault: () => void }

/**
 * Toasts (sonner) sit above every modal. A click on one is "outside" the
 * dialog to Radix, which would dismiss the dialog, so those events are
 * swallowed before the caller's own handler runs.
 */
export function keepOpenOnToastInteraction<E extends OutsideEvent>(
  handler?: (event: E) => void
) {
  return (event: E) => {
    const target = event.target as Element | null
    if (target && typeof target.closest === "function" && target.closest("[data-sonner-toaster]")) {
      event.preventDefault()
      return
    }
    handler?.(event)
  }
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, stacked = false, hideCloseButton = false, size = "default", onPointerDownOutside, onInteractOutside, onFocusOutside, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={stacked ? 'z-[60]' : undefined} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-card text-card-foreground p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        stacked ? 'z-[60]' : 'z-50',
        size === "confirm" ? confirmCardClass : null,
        className,
        size === "confirm" ? null : phoneFullScreenClass
      )}
      {...props}
      onPointerDownOutside={keepOpenOnToastInteraction(onPointerDownOutside)}
      onInteractOutside={keepOpenOnToastInteraction(onInteractOutside)}
      onFocusOutside={keepOpenOnToastInteraction(onFocusOutside)}
    >
      <InsideDialogContext.Provider value={true}>{children}</InsideDialogContext.Provider>
      {hideCloseButton ? null : (
        <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground after:absolute after:-inset-3 after:content-[''] md:after:hidden">
          <X className="h-5 w-5 sm:h-4 sm:w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/** Compact header bar (Messages modal style): bordered, tight padding, room for close button. */
export const compactDialogHeaderClass =
  "space-y-0 px-4 py-2.5 pr-12 border-b border-border shrink-0 bg-card"

export const compactDialogTitleClass =
  "text-base font-semibold leading-tight tracking-tight"

export const compactDialogDescriptionClass =
  "text-xs text-muted-foreground mt-0.5"

/** Shell: no default padding; header + scrollable body + optional footer. */
export const compactDialogShellClass =
  "flex flex-col gap-0 p-0 overflow-hidden"

export const compactDialogBodyClass =
  "compact-dialog-body flex-1 min-h-0 overflow-y-auto px-4 py-3 bg-background"

export const compactDialogFooterClass =
  "shrink-0 px-4 py-3 border-t border-border bg-card"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

type DialogCompactHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  title: React.ReactNode
  description?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
  actions?: React.ReactNode
}

function DialogCompactHeader({
  title,
  description,
  icon: Icon,
  actions,
  children,
  className,
  ...props
}: DialogCompactHeaderProps) {
  return (
    <DialogHeader className={cn(compactDialogHeaderClass, className)} {...props}>
      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <DialogTitle
            className={cn(
              Icon && "flex items-center gap-1.5",
              compactDialogTitleClass
            )}
          >
            {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
            {typeof title === "string" ? (
              <span className={Icon ? "truncate" : undefined}>{title}</span>
            ) : (
              title
            )}
          </DialogTitle>
          {description != null && description !== "" ? (
            typeof description === "string" ? (
              <p className={compactDialogDescriptionClass}>{description}</p>
            ) : (
              description
            )
          ) : null}
        </div>
        {actions}
      </div>
      {children}
    </DialogHeader>
  )
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogCompactHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
